(function () {
  var SECTION_SELECTOR = '[data-hero-carousel]';
  var INIT_FLAG = 'heroCarouselInit';

  var SWIPE_THRESHOLD = 50;
  /* Movement past this counts as a drag rather than a click. */
  var CLICK_SLOP = 8;

  function initSection(section) {
    if (!section || section.dataset[INIT_FLAG] === '1') return;
    section.dataset[INIT_FLAG] = '1';

    /* The section's own inline script shuffles on first paint and leaves the flag behind.
       A theme-editor re-render arrives without that script having run, and every slide in
       it ships data-src only, so the hero would come back blank — reuse the same
       implementation here rather than keeping a second copy of it. */
    if (
      section.hasAttribute('data-hero-randomize') &&
      !section.hasAttribute('data-hero-shuffled') &&
      typeof window.heroCarouselShuffle === 'function'
    ) {
      window.heroCarouselShuffle(section);
    }

    var stage = section.querySelector('[data-hero-stage]');
    var rail = section.querySelector('[data-hero-rail]');
    var track = section.querySelector('[data-hero-rail-track]');
    if (!stage) return;

    var slides = Array.prototype.slice.call(section.querySelectorAll('[data-hero-slide]'));
    var count = slides.length;
    if (!count) return;

    var thumbs = track
      ? Array.prototype.slice.call(track.querySelectorAll('[data-hero-thumb]'))
      : [];

    var mobile = window.matchMedia ? window.matchMedia('(max-width: 749px)') : null;

    /* Keep the headline on the same vertical line as the logo. .header__columns is the box
       the logo and menu items actually sit in — not the wider .header__row around it, which
       runs 16px past it on mobile and 1050px past it at 2560.

       Measured rather than recomputed, because the theme derives that box from a section
       grid inset, a conditional page margin and a max-width cap that change at different
       widths; reimplementing the arithmetic drifted by 41px between 1280 and 1500. */
    var navColumns = document.querySelector('.header__columns');

    /* Only the band width is measured now. The header's height used to be measured too,
       to size the mobile hero as `100dvh - header`, but that sizing is gone: the stage
       holds a fixed ratio so its height cannot move while the reader scrolls. */
    var syncHeaderMetrics = function () {
      if (navColumns) {
        var w = navColumns.getBoundingClientRect().width;
        if (w > 0) section.style.setProperty('--hero-band-width', Math.round(w) + 'px');
      }
    };

    syncHeaderMetrics();
    if (window.ResizeObserver && navColumns) {
      new ResizeObserver(syncHeaderMetrics).observe(navColumns);
    } else {
      window.addEventListener('resize', syncHeaderMetrics);
    }
    var reduceMotion = window.matchMedia
      ? window.matchMedia('(prefers-reduced-motion: reduce)')
      : null;
    var prefersReduced = function () {
      return Boolean(reduceMotion && reduceMotion.matches);
    };

    var autoplayEnabled = stage.hasAttribute('data-hero-autoplay') && count > 1;
    var speed = parseFloat(getComputedStyle(stage).getPropertyValue('--hero-autoplay-speed')) || 6;
    var current = 0;
    var timer = null;
    var started = false;
    var hovered = false;
    /* A pointer gesture is in flight: a swipe on the stage or a drag on the rail. */
    var interacting = false;
    var paused = false;
    /* Milliseconds left in the current countdown, preserved across pauses. */
    var remaining = 0;
    var cycleStartedAt = 0;

    /* ── Slides ─────────────────────────────────────────────────────────── */

    /* Stacked slides all sit inside the viewport, and Chrome fetches a lazy image the
       moment its element is display:none, so loading="lazy" defers nothing here. Slides
       past the first two therefore ship no src at all — promoting data-src on mount is
       what actually keeps them off the wire until they are needed. */
    function mount(index) {
      var slide = slides[index];
      if (!slide) return;
      slide.classList.add('is-mounted');

      var img = slide.querySelector('.hero-carousel__img[data-src]');
      if (!img) return;
      var srcset = img.getAttribute('data-srcset');
      if (srcset) img.setAttribute('srcset', srcset);
      img.setAttribute('src', img.getAttribute('data-src'));
      img.removeAttribute('data-src');
      img.removeAttribute('data-srcset');
    }

    /* Holds the outgoing slide at full opacity underneath the incoming fade. Without it
       the old image would vanish the instant the new one started fading in, and the
       viewport background would show through until the fade finished. */
    function markLeaving(slide) {
      if (!slide) return;
      slide.classList.add('is-leaving');
    }

    function clearLeaving() {
      for (var i = 0; i < count; i++) {
        slides[i].classList.remove('is-leaving');
      }
    }

    /* Released on the INCOMING slide's transitionend, not the outgoing one's: the outgoing
       slide no longer animates anything, so it has no transition of its own to wait on.
       Keying off the real fade rather than a hardcoded delay means this cannot drift out of
       step with --hero-fade-duration in the stylesheet. */
    for (var s = 0; s < count; s++) {
      slides[s].addEventListener('transitionend', function (event) {
        if (event.propertyName !== 'opacity') return;
        if (!event.currentTarget.classList.contains('is-active')) return;
        clearLeaving();
      });
    }

    function setActive(index) {
      for (var i = 0; i < count; i++) {
        var isActive = i === index;
        var wasActive = slides[i].classList.contains('is-active');

        if (isActive) {
          /* Covers re-selecting a slide that is still on its way out — it must lose
             is-leaving or the class would outlive the fade it belongs to. */
          slides[i].classList.remove('is-leaving');
        } else if (wasActive) {
          markLeaving(slides[i]);
        } else {
          /* Exactly one slide may sit under the incoming fade. Interrupting a fade —
             clicking a thumbnail part way through one — otherwise strands the previous
             leaver at full opacity, and two opaque slides sharing z-index 0 swap places
             visibly under the next fade depending on document order. */
          slides[i].classList.remove('is-leaving');
        }

        slides[i].classList.toggle('is-active', isActive);
        slides[i].inert = !isActive;
        if (isActive) {
          slides[i].removeAttribute('aria-hidden');
        } else {
          slides[i].setAttribute('aria-hidden', 'true');
        }
      }
      for (var t = 0; t < thumbs.length; t++) {
        if (Number(thumbs[t].getAttribute('data-slide-index')) === index) {
          thumbs[t].setAttribute('aria-selected', 'true');
        } else {
          thumbs[t].removeAttribute('aria-selected');
        }
      }
      syncSlideLinked(index);
    }

    /* Gates the "view work" pill and the cursor: none that comes with it. A slide only
       wraps a link when its block has one, and taking the reader's cursor away to promise
       "view work" over a slide that cannot be clicked is worse than showing no pill. */
    function syncSlideLinked(index) {
      if (slides[index] && slides[index].querySelector('.hero-carousel__slide-link')) {
        stage.setAttribute('data-hero-slide-linked', '');
      } else {
        stage.removeAttribute('data-hero-slide-linked');
      }
    }

    /* Slide 0 is marked is-active in the Liquid, so setActive never runs for it. */
    syncSlideLinked(current);

    function goTo(index) {
      var next = ((index % count) + count) % count;
      if (next === current) return;

      mount(next);
      mount((next + 1) % count);

      /* A slide mounted this frame has just come out of display:none and has no resolved
         opacity to animate from, so let a frame land or the fade is skipped entirely. */
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          current = next;
          setActive(next);
          moveRail(next);
          /* The progress bar restarts whenever aria-selected moves, so the countdown
             has to restart with it — otherwise picking a thumbnail leaves the bar and
             the timer running to different deadlines. */
          startCycle();
        });
      });
    }

    function next() {
      goTo(current + 1);
    }

    /* ── Autoplay ───────────────────────────────────────────────────────── */

    /* A self-rescheduling timeout, not setInterval. An interval cannot be paused — the
       old code could only skip a tick while hovered, so the countdown kept its original
       schedule while the progress bar was frozen, and the two drifted apart by however
       long the pause lasted. Tracking the remaining time lets a pause actually defer the
       change, so the bar and the timer always agree.

       Two attributes carry the state, and each mirrors exactly what the CSS does:
         [data-hero-playing] — a countdown exists at all. Removing it drops the progress
             animation entirely, so the fill resets to zero; `remaining` resets to match.
         [data-hero-paused]  — the countdown is held. CSS pauses the animation in place,
             so `remaining` is preserved rather than reset. */

    function cycleMs() {
      return speed * 1000;
    }

    function clearTimer() {
      if (timer) clearTimeout(timer);
      timer = null;
    }

    function armTimer(ms) {
      clearTimer();
      remaining = ms;
      cycleStartedAt = Date.now();
      timer = setTimeout(function () {
        timer = null;
        next();
      }, ms);
    }

    /* Called after every slide change, so selecting a thumbnail restarts the countdown
       the same way it restarts the progress bar. Under setInterval it did not, and the
       bar could finish seconds before or after the slide actually moved. */
    function startCycle() {
      if (!autoplayEnabled || prefersReduced() || !started) return;
      if (!stage.hasAttribute('data-hero-playing')) return;
      if (paused) {
        clearTimer();
        remaining = cycleMs();
        return;
      }
      armTimer(cycleMs());
    }

    function stop() {
      clearTimer();
      remaining = 0;
      stage.removeAttribute('data-hero-playing');
    }

    function play() {
      if (!autoplayEnabled || prefersReduced()) return;
      stage.setAttribute('data-hero-playing', '');
      if (timer || paused) return;
      armTimer(remaining > 0 ? remaining : cycleMs());
    }

    function setPaused(value) {
      if (paused === value) return;
      paused = value;

      if (value) {
        stage.setAttribute('data-hero-paused', '');
        if (timer) {
          remaining = Math.max(0, remaining - (Date.now() - cycleStartedAt));
          clearTimer();
        }
        return;
      }

      stage.removeAttribute('data-hero-paused');
      if (!timer && stage.hasAttribute('data-hero-playing')) {
        armTimer(remaining > 0 ? remaining : cycleMs());
      }
    }

    /* Every reason to hold the countdown goes through here, so two of them overlapping
       cannot leave the carousel paused after only one has cleared. */
    function syncPaused() {
      setPaused(hovered || interacting || document.hidden);
    }

    /* A hidden tab holds the countdown rather than skipping through it, matching what
       the browser does to the CSS animations behind it. */
    document.addEventListener('visibilitychange', syncPaused);

    /* Releasing a gesture is bound to the window, not to the element that started it: a
       finger lifted outside the stage never fires pointerup on the stage, and a hold that
       is never released is the stuck-autoplay bug all over again. Both gesture handlers
       below share this one release path. */
    function releaseInteraction() {
      if (!interacting) return;
      interacting = false;
      syncPaused();
    }

    window.addEventListener('pointerup', releaseInteraction);
    window.addEventListener('pointercancel', releaseInteraction);

    /* ── Reveal gate ────────────────────────────────────────────────────────
       Cycling must not begin while the image is still cut off by the fold. Watching
       the stage's bottom edge means "the whole image is on screen" — which is the
       first scroll on a normal display, and immediately on one tall enough to show
       the hero at load. The same observer pauses when the hero leaves and resumes on
       the slide it left off. */
    if (autoplayEnabled && 'IntersectionObserver' in window) {
      var bottomSentinel = new IntersectionObserver(
        function (entries) {
          for (var i = 0; i < entries.length; i++) {
            if (!entries[i].isIntersecting) continue;
            started = true;
            play();
            bottomSentinel.disconnect();
          }
        },
        { rootMargin: '0px 0px 0px 0px', threshold: 0 }
      );
      /* Observe a zero-height marker pinned to the bottom of the stage. */
      var sentinel = document.createElement('span');
      sentinel.className = 'hero-carousel__sentinel';
      sentinel.setAttribute('aria-hidden', 'true');
      stage.appendChild(sentinel);
      bottomSentinel.observe(sentinel);

      var presence = new IntersectionObserver(
        function (entries) {
          for (var i = 0; i < entries.length; i++) {
            if (!started) continue;
            if (entries[i].isIntersecting) {
              play();
            } else {
              stop();
            }
          }
        },
        { threshold: 0 }
      );
      presence.observe(stage);
    } else if (autoplayEnabled) {
      started = true;
      play();
    }

    /* Only the thumbnail strip pauses the carousel; hovering the photograph does not.
       The whole rail is the target rather than each thumbnail, because the gaps between
       cards would otherwise resume and re-pause the countdown as the cursor scans along
       the row.

       setPaused holds the countdown and sets [data-hero-paused], which is what freezes
       the progress fill and the image zoom. One call drives all three, so they stop and
       resume on the same frame instead of drifting apart.

       pointerenter/pointerleave with a pointerType guard, not mouseenter/mouseleave: a tap
       on a touchscreen fires mouseenter as a compatibility event, and no mouseleave ever
       follows it, so hover was latched on for the rest of the page's life and autoplay
       never resumed after a thumbnail tap. Reading pointerType keeps that phantom hover
       out while a real mouse on a touchscreen laptop still pauses, which a (hover: hover)
       media query would not. */
    if (rail) {
      rail.addEventListener('pointerenter', function (event) {
        if (event.pointerType !== 'mouse') return;
        hovered = true;
        syncPaused();
      });
      rail.addEventListener('pointerleave', function (event) {
        if (event.pointerType !== 'mouse') return;
        hovered = false;
        syncPaused();
      });
    }

    /* ── "View work" pill ───────────────────────────────────────────────────
       Positions the pill under the cursor while the photograph is hovered. The reveal
       itself is CSS (.hero-carousel__slides:hover), so this only ever writes coordinates
       — if it throws or never runs, the pill simply stays at its resting position and
       nothing else in the carousel is affected. Wrapped anyway, because autoplay and the
       swipe gesture are set up after this point and must not be taken down by it.

       Deliberately does NOT touch the hovered flag: hovering the image leaves autoplay
       running, same as before the pill existed. */
    try {
      var slidesBox = section.querySelector('[data-hero-slides]');
      var viewWork = section.querySelector('[data-hero-view-work]');
      var finePointer = window.matchMedia && window.matchMedia('(pointer: fine)').matches;

      if (slidesBox && viewWork && finePointer) {
        var lastX = 0;
        var lastY = 0;
        var moveRaf = 0;

        /* Percentages, not pixels: .hero-carousel__slides is the pill's containing block
           and both are inset: 0 on the same box, so a fraction of the box maps straight
           onto left/top with nothing to keep in sync on resize.

           Clamped inside the frame because .hero-carousel__slide is overflow: hidden —
           right at an edge an unclamped pill gets sliced in half. */
        function placePill(clientX, clientY) {
          var box = slidesBox.getBoundingClientRect();
          if (!box.width || !box.height) return;

          var relX = Math.min(0.95, Math.max(0.05, (clientX - box.left) / box.width));
          var relY = Math.min(0.95, Math.max(0.05, (clientY - box.top) / box.height));

          viewWork.style.left = relX * 100 + '%';
          viewWork.style.top = relY * 100 + '%';
        }

        /* One placement per frame at most. The coordinates are overwritten rather than
           dropped while a frame is pending, so the pill renders where the cursor is now
           and not where it was when the frame was queued. */
        slidesBox.addEventListener(
          'mousemove',
          function (event) {
            lastX = event.clientX;
            lastY = event.clientY;
            if (moveRaf) return;
            moveRaf = requestAnimationFrame(function () {
              moveRaf = 0;
              placePill(lastX, lastY);
            });
          },
          { passive: true }
        );

        /* Placed before the CSS reveal runs, so the pill fades in under the cursor. Left
           to the first mousemove it would fade in wherever the previous hover ended and
           then jump. The rect is read fresh on every placement, so scrolling the hero
           while hovering cannot leave the pill behind either. */
        slidesBox.addEventListener('mouseenter', function (event) {
          placePill(event.clientX, event.clientY);
        });

        slidesBox.addEventListener('mouseleave', function () {
          if (moveRaf) cancelAnimationFrame(moveRaf);
          moveRaf = 0;
        });
      }
    } catch (e) {
      /* Decorative. Never worth taking the carousel down for. */
    }

    /* ── Input ──────────────────────────────────────────────────────────── */

    if (track) {
      track.addEventListener('click', function (event) {
        var thumb = event.target.closest('[data-hero-thumb]');
        if (!thumb) return;
        goTo(Number(thumb.getAttribute('data-slide-index')));
      });
    }

    stage.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        goTo(current + 1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goTo(current - 1);
      }
    });

    /* Stacked slides cannot follow a finger the way a scroll container does, so the
       drag is read as a gesture: past the threshold it commits, otherwise nothing. */
    var pointerStartX = null;
    var pointerStartY = null;
    var swiped = false;

    stage.addEventListener('pointerdown', function (event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      pointerStartX = event.clientX;
      pointerStartY = event.clientY;
      swiped = false;
      /* Hold the countdown for the length of the gesture. Desktop got this from hovering
         the rail, but a finger produces no hover, so on touch the slide used to keep
         changing underneath the swipe. Released on the window (see releaseInteraction). */
      interacting = true;
      syncPaused();
    });
    stage.addEventListener('pointerup', function (event) {
      if (pointerStartX === null) return;
      var dx = event.clientX - pointerStartX;
      var dy = event.clientY - pointerStartY;
      pointerStartX = null;

      /* Any real drag suppresses the click that follows. The slide is wrapped in a link,
         so without this a vertical drag across the image navigates to the case study. */
      if (Math.max(Math.abs(dx), Math.abs(dy)) > CLICK_SLOP) swiped = true;

      if (Math.abs(dx) < SWIPE_THRESHOLD) return;
      /* Only commit when the gesture is predominantly sideways. Without this a diagonal
         flick while scrolling the page changes the slide by accident, which on a phone
         is most of them. */
      if (Math.abs(dx) <= Math.abs(dy)) return;
      goTo(dx < 0 ? current + 1 : current - 1);
    });
    stage.addEventListener('pointercancel', function () {
      pointerStartX = null;
    });
    /* preventDefault on pointerup does not stop the click that follows it, so a
       committed swipe has to be swallowed here or it also follows the slide link. */
    stage.addEventListener('click', function (event) {
      if (!swiped) return;
      swiped = false;
      event.preventDefault();
      event.stopPropagation();
    }, true);

    /* ── Rail ───────────────────────────────────────────────────────────── */

    /* The rail is both an indicator and something you can grab. Position is a single
       continuous pixel offset rather than a slide index, so a free drag and a snap to a
       thumbnail are the same mechanism. `virtual` is the position in the tripled track;
       keeping it inside the middle copy leaves a full copy of runway either side, which
       is what lets the rail loop forever instead of ending on the last thumbnail. */
    var virtual = count;
    var offset = 0;

    function pitch() {
      return thumbs[1] ? thumbs[1].offsetLeft - thumbs[0].offsetLeft : 0;
    }

    /* How far the active thumbnail sits from the left edge of the rail window. The two
       breakpoints want different answers — the desktop frame shows one full thumbnail
       of lead-in, the mobile frame centres the active one — and expressing that as a
       single number keeps every other calculation breakpoint-agnostic. */
    function lead() {
      if (!thumbs.length) return 0;
      return mobile && mobile.matches
        ? (rail.clientWidth - thumbs[0].offsetWidth) / 2
        : pitch();
    }

    /* thumbs[0].offsetLeft carries the track's padding (16px on mobile, 0 on desktop),
       so these hold at both breakpoints without a special case. */
    function restingOffset(v) {
      return thumbs[0].offsetLeft + v * pitch() - lead();
    }

    function virtualForOffset(o) {
      return Math.round((o + lead() - thumbs[0].offsetLeft) / pitch());
    }

    function applyRail(animate) {
      if (!track || !thumbs.length) return;
      track.style.transition = animate ? '' : 'none';
      track.style.transform = 'translate3d(' + -offset + 'px, 0, 0)';
      if (!animate) {
        void track.offsetWidth; /* flush, so the next move animates again */
        track.style.transition = '';
      }
    }

    /* Wrap back into the middle copy whenever the offset drifts into an outer one. The
       position is visually identical, so the jump is invisible — this is what makes the
       loop seamless in both directions, dragged or automatic. Returns the shift applied
       so a drag in progress can correct its own origin by the same amount. */
    function normaliseOffset() {
      var span = count * pitch();
      if (span <= 0) return 0;
      var shift = 0;
      while (offset >= restingOffset(count * 2)) {
        offset -= span;
        shift -= span;
      }
      while (offset < restingOffset(count)) {
        offset += span;
        shift += span;
      }
      virtual = virtualForOffset(offset);
      return shift;
    }

    /* Pick the copy of `index` nearest the rail's current position. Deriving it from
       where the rail actually is — rather than stepping by a delta — keeps automatic
       advances correct even after the user has dragged the strip somewhere else. */
    function moveRail(index) {
      if (!track || !pitch()) return;
      virtual = Math.round((virtual - index) / count) * count + index;
      offset = restingOffset(virtual);
      applyRail(true);
    }

    function snapRail() {
      if (!pitch()) return;
      virtual = virtualForOffset(offset);
      offset = restingOffset(virtual);
      applyRail(true);
    }

    if (track) {
      track.addEventListener('transitionend', function (event) {
        if (event.propertyName === 'transform') {
          if (normaliseOffset()) applyRail(false);
        }
      });
      window.addEventListener('resize', function () {
        offset = restingOffset(virtual);
        applyRail(false);
      });
      /* Crossing 750px swaps centred anchoring for lead-in anchoring, so the rail has
         to be re-placed or it stays parked at the other breakpoint's position. */
      if (mobile && mobile.addEventListener) {
        mobile.addEventListener('change', function () {
          offset = restingOffset(virtual);
          applyRail(false);
        });
      }
      offset = restingOffset(virtual);
      applyRail(false);
    }

    /* ── Rail input ─────────────────────────────────────────────────────────
       Drag scrolls the strip freely without changing the hero; clicking a thumbnail is
       what selects. Browsing ahead and committing are separate actions, so looking
       around never yanks the image out from under you. The drag holds the countdown
       itself, via interacting. Hovering the stage does not pause anything, and on touch
       there is no hover to rely on at all. */

    if (rail && track) {
      var DRAG_START = 4;
      var railPointerId = null;
      var railDragging = false;
      var railStartX = 0;
      var railStartOffset = 0;
      var railMoved = 0;

      rail.addEventListener('pointerdown', function (event) {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        if (!pitch()) return;
        /* Keep this off the stage's swipe handler, or dragging the strip would change
           the hero slide instead of moving the thumbnails. */
        event.stopPropagation();
        railPointerId = event.pointerId;
        railDragging = false;
        railMoved = 0;
        railStartX = event.clientX;
        railStartOffset = offset;
        /* Same hold as the stage swipe: a dragged strip must not fight an advancing hero.
           On a mouse the hover above has already paused, and both go through syncPaused,
           so whichever clears first leaves the other holding. */
        interacting = true;
        syncPaused();
      });

      rail.addEventListener('pointermove', function (event) {
        if (railPointerId === null || event.pointerId !== railPointerId) return;
        var dx = event.clientX - railStartX;
        railMoved = Math.max(railMoved, Math.abs(dx));

        if (!railDragging) {
          if (railMoved <= DRAG_START) return;
          railDragging = true;
          /* Capture only once this is definitely a drag. Capturing on pointerdown
             retargets the click that follows to the rail itself, so the thumbnail
             under the cursor never receives it and selecting a slide silently fails. */
          rail.setPointerCapture(railPointerId);
        }

        offset = railStartOffset - dx;
        /* Correct the drag origin by whatever the wrap shifted, so the strip keeps
           tracking the pointer 1:1 across the seam. */
        railStartOffset += normaliseOffset();
        applyRail(false);
      });

      function endRailDrag() {
        if (railPointerId === null) return;
        if (railDragging) {
          if (rail.hasPointerCapture(railPointerId)) rail.releasePointerCapture(railPointerId);
          snapRail();
        }
        railPointerId = null;
        railDragging = false;
      }

      rail.addEventListener('pointerup', endRailDrag);
      rail.addEventListener('pointercancel', endRailDrag);

      /* Swallow the click that follows a real drag, so letting go over a thumbnail
         does not also select it. */
      rail.addEventListener(
        'click',
        function (event) {
          if (railMoved <= DRAG_START) return;
          railMoved = 0;
          event.preventDefault();
          event.stopPropagation();
        },
        true
      );

      /* Horizontal wheel and trackpad swipes move the strip. Vertical is deliberately
         left alone so the page still scrolls normally when the cursor is over the rail. */
      rail.addEventListener(
        'wheel',
        function (event) {
          if (Math.abs(event.deltaX) <= Math.abs(event.deltaY) || !pitch()) return;
          event.preventDefault();
          offset += event.deltaX;
          normaliseOffset();
          applyRail(false);
          clearTimeout(rail._heroSnap);
          rail._heroSnap = setTimeout(snapRail, 120);
        },
        { passive: false }
      );
    }

    /* Liquid only marks the middle copy's first thumbnail, so sync all three copies up
       front — otherwise the lead-in and trailing copies stay dimmed until the first move. */
    setActive(current);

    if (reduceMotion && reduceMotion.addEventListener) {
      reduceMotion.addEventListener('change', function () {
        if (prefersReduced()) {
          stop();
        } else if (started) {
          play();
        }
      });
    }
  }

  function initAll(root) {
    var container = root && root.querySelectorAll ? root : document;
    var sections = container.querySelectorAll
      ? container.querySelectorAll(SECTION_SELECTOR)
      : [];
    Array.prototype.forEach.call(sections, initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var container = e.target;
    var sections = container.querySelectorAll
      ? container.querySelectorAll(SECTION_SELECTOR)
      : [];
    Array.prototype.forEach.call(sections, function (section) {
      delete section.dataset[INIT_FLAG];
    });
  });
})();
